import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../../ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../Config/config";
import FullscreenGallery from "./FullscreenGallery";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Activity = ({ name, mainImage, onClick, size }) => {
  const { themes, currentTheme } = useTheme();
  const theme = themes[currentTheme];

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        flex: "0 0 auto",
        width: `${size}px`,
        marginBottom: "16px",
      }}
    >
      <motion.div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: "50%",
          overflow: "hidden",
          border: `4px solid ${theme.accentColor}`,
          boxSizing: "border-box",
        }}
      >
        <img
          src={mainImage || "https://via.placeholder.com/96"}
          alt={name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
            border: "3px solid white",
          }}
        />
      </motion.div>
      <motion.span
        style={{
          marginTop: "8px",
          fontSize: "14px",
          fontWeight: "600",
          color: theme.textColor,
          textAlign: "center",
        }}
        whileHover={{ color: theme.accentColor }}
      >
        {name}
      </motion.span>
    </motion.div>
  );
};

const SeeAllLink = ({ onClick, style }) => {
  const { themes, currentTheme } = useTheme();
  const theme = themes[currentTheme];

  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      style={{
        color: theme.accentColor,
        textDecoration: "none",
        fontWeight: "bold",
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      See All →
    </a>
  );
};

const Activities = ({ selectedLocation, selectedLocationType }) => {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const { themes, currentTheme } = useTheme();
  const theme = themes[currentTheme];
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const activitiesCollection = collection(db, "activities");
        const activitiesSnapshot = await getDocs(activitiesCollection);
        const activitiesList = activitiesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setActivities(activitiesList);
      } catch (error) {
        console.error("Error fetching activities:", error);
      }
    };

    fetchActivities();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      const filtered = activities.filter((activity) =>
        activity.activityLocation?.toLowerCase().includes(selectedLocation.toLowerCase())
      );
      setFilteredActivities(filtered);
    } else if (selectedLocationType) {
      const filtered = activities.filter(
        (activity) =>
          activity.activityLocationType?.toLowerCase() ===
          selectedLocationType.toLowerCase()
      );
      setFilteredActivities(filtered);
    } else {
      setFilteredActivities(activities);
    }
    setCurrentIndex(0);
  }, [selectedLocation, selectedLocationType, activities]);

  if (filteredActivities.length === 0) {
    return null;
  }

  const isMobile = windowWidth < 768;
  const containerWidth = isMobile ? windowWidth - 40 : windowWidth - 80;
  const activitySize = isMobile ? Math.floor((containerWidth - 32) / 3) : 130;

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => Math.max(0, prevIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      Math.min(filteredActivities.length - 6, prevIndex + 1)
    );
  };

  const visibleActivities = isMobile
    ? filteredActivities.slice(0, 3)
    : filteredActivities.slice(currentIndex, currentIndex + 6);

  return (
    <div
      style={{
        textAlign: "center",
        backgroundColor: theme.bgColor,
        padding: "20px",
        position: "relative",
      }}
    >
      {isMobile ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "32px",
          }}
        >
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: theme.textColor,
              margin: 0,
              textAlign: "left",
            }}
          >
            ACTIVITIES
          </h1>
          <SeeAllLink onClick={() => setShowAllActivities(true)} />
        </div>
      ) : (
        <>
          <h1
            style={{
              fontSize: "32px",
              fontWeight: "bold",
              marginBottom: "32px",
              color: theme.textColor,
            }}
          >
            ACTIVITIES
          </h1>
        </>
      )}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
        }}
      >
        {!isMobile && (
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textColor,
              fontSize: "24px",
              zIndex: 1,
            }}
          >
            <ChevronLeft size={24} />
          </button>
        )}
        <div
          ref={scrollContainerRef}
          style={{
            display: "flex",
            flexWrap: "nowrap",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          {visibleActivities.map((activity) => (
            <Activity
              key={activity.id}
              name={activity.activityName}
              mainImage={
                activity.imagesArray?.[0] || "https://via.placeholder.com/96"
              }
              onClick={() => setSelectedActivity(activity)}
              size={activitySize}
            />
          ))}
          {!isMobile && (
            <SeeAllLink
              onClick={() => setShowAllActivities(true)}
              style={{
                width: `${activitySize}px`,
                height: `${activitySize}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          )}
        </div>
        {!isMobile && (
          <button
            onClick={handleNext}
            disabled={currentIndex >= filteredActivities.length - 6}
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textColor,
              fontSize: "24px",
              zIndex: 1,
            }}
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>
      <AnimatePresence>
        {selectedActivity && (
          <FullscreenGallery
            isOpen={!!selectedActivity}
            onRequestClose={() => setSelectedActivity(null)}
            activity={selectedActivity}
          />
        )}
      </AnimatePresence>
      {showAllActivities && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: theme.bgColor,
            zIndex: 1000,
            overflow: "auto",
            padding: "20px",
          }}
        >
          <button
            onClick={() => setShowAllActivities(false)}
            style={{
              position: "absolute",
              top: "20px",
              left: "20px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textColor,
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ChevronLeft size={24} /> Back
          </button>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              marginBottom: "20px",
              color: theme.textColor,
            }}
          >
            All Activities in {selectedLocation || selectedLocationType}
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            {filteredActivities.map((activity) => (
              <Activity
                key={activity.id}
                name={activity.activityName}
                mainImage={
                  activity.imagesArray?.[0] || "https://via.placeholder.com/96"
                }
                onClick={() => setSelectedActivity(activity)}
                size={isMobile ? activitySize : 130}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Activities;